import React from 'react';
import { useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import BlockerReviewApprovalForm from './BlockerReviewApprovalForm';
import { IBlockerReviewApproval, IEntityTerminationActionItem } from '../types';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import { APPROVE_ENTITY_TERMINATION_BLOCKER_REVIEW } from '../../../utils/statApi/EntityTerminationApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';

interface IBlockerReviewApprovalPanelProps {
  closePanel: () => void;
  data: IBlockerReviewApproval;
  blockerItems: IEntityTerminationActionItem[];
}

const BlockerReviewApprovalPanel: React.FC<IBlockerReviewApprovalPanelProps> = ({
  closePanel,
  data,
  blockerItems,
}) => {
  const form = useForm<IBlockerReviewApproval>();
  const { handleSubmit } = form;

  const [approveBlockerReview, { loading, error }] = useMutation(
    APPROVE_ENTITY_TERMINATION_BLOCKER_REVIEW,
    {
      refetchQueries: ['BlockerQueueReview'],
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IBlockerReviewApproval): void => {
    approveBlockerReview({
      variables: {
        blockerReviewInputDto: {
          reviewId: data.reviewId,
          approvalId: data.review.reviewApprovals?.[0]?.id,
          terminationLifecycleStatusId: formData.terminationLifecycleStatus?.id,
        },
      },
    });
  };

  const hasIncompleteBlockers = blockerItems.some(
    (item) => !item.dateCompleted || (item.tasks ?? []).some((task) => !task.completedDate),
  );

  const isSaveDisabled = hasIncompleteBlockers;

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Approve Blocker Review"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Approve"
          saveTitle="Approve"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
          mutationLoading={loading}
          disabled={isSaveDisabled}
        />
      )}
    >
      <form>
        <LoadingErrorMessage error={error} loading={loading} />
        <BlockerReviewApprovalForm form={form} data={data} blockerItems={blockerItems} />
      </form>
    </CoherencePanel>
  );
};

export default BlockerReviewApprovalPanel;
